import { getLevel, useAuth } from '@/contexts/AuthContext';
import {
  useManageSubscription,
  useTokenPackPurchase,
} from '@/services/subscriptionService';
import { useToast } from '@/hooks/use-toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { THUMBNAIL_URLS_QUERY_KEY } from '@/lib/conversationsQuery';
import { useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useProfile, useUpdateProfile } from '@/services/profileService';
import { useTokenPacks } from '@/hooks/useTokenPacks';
import { useDimensionPreferences } from '@/hooks/useDimensionPreferences';
import { formatPeriodEnd } from '@/views/settings/formatPeriodEnd';
import { AccountSection } from '@/views/settings/AccountSection';
import { DimensionsSection } from '@/views/settings/DimensionsSection';
import { NotificationsSection } from '@/views/settings/NotificationsSection';
import { BillingSection } from '@/views/settings/BillingSection';
import { AdminSection } from '@/views/settings/AdminSection';
import { DataPrivacySection } from '@/views/settings/DataPrivacySection';
import { SettingsFooter } from '@/views/settings/SettingsFooter';

export default function SettingsView() {
  const { billing, user, resetPassword } = useAuth();
  const level = getLevel(billing);
  const freeTokens = billing?.tokens.free ?? 0;
  const subscriptionTokens = billing?.tokens.subscription ?? 0;
  const purchasedTokens = billing?.tokens.purchased ?? 0;
  const totalTokens = billing?.tokens.total ?? 0;
  const periodEnd = formatPeriodEnd(
    billing?.subscription?.currentPeriodEnd ?? null,
  );
  const { data: profile } = useProfile();
  const { mutate: updateProfile, isPending: isUpdateLoading } =
    useUpdateProfile();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const ADMIN_EMAILS = new Set(['jack@teranis.ai']);
  const isAdmin = !!user?.email && ADMIN_EMAILS.has(user.email);

  const { mutate: bumpThumbnails, isPending: isBumping } = useMutation({
    mutationFn: async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/bump-thumbnails`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );
      if (!res.ok) throw new Error(await res.text());
      return res.json() as Promise<{ version: string; deleted: number }>;
    },
    onSuccess: ({ version, deleted }) => {
      if (user?.id) {
        queryClient.invalidateQueries({
          queryKey: THUMBNAIL_URLS_QUERY_KEY(user.id),
        });
      }
      toast({
        title: 'Thumbnails queued for regeneration',
        description: `Version bumped to ${version}. ${deleted} cached thumbnails deleted. Users will see fresh renders on next visit.`,
      });
    },
    onError: (e) => {
      Sentry.captureException(e);
      toast({
        title: 'Error',
        description: 'Failed to bump thumbnails',
        variant: 'destructive',
      });
    },
  });
  const [newName, setNewName] = useState(profile?.full_name || '');
  const [editingName, setEditingName] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const { data: tokenPacks = [] } = useTokenPacks();
  const { prefs: dimensionPrefs, setMode: setDimensionMode } =
    useDimensionPreferences();
  const {
    mutate: purchaseTokenPack,
    isPending: isPurchaseLoading,
    variables: purchaseVariables,
  } = useTokenPackPurchase();

  useEffect(() => {
    if (editingName) {
      nameInputRef.current?.focus();
    }
  }, [editingName]);

  useEffect(() => {
    setNewName(profile?.full_name || '');
  }, [profile?.full_name]);

  const { mutate: handleManageSubscription, isPending: isManageLoading } =
    useManageSubscription();

  const handleUpdateName = () => {
    updateProfile(
      { full_name: newName },
      {
        onSuccess: () => {
          setEditingName(false);
          setNewName(profile?.full_name || '');
          toast({
            title: 'Success',
            description: 'Your name has been updated',
          });
        },
        onError: (e) => {
          Sentry.captureException(e);
          toast({
            title: 'Error',
            description: 'Failed to update name',
            variant: 'destructive',
          });
        },
      },
    );
  };

  const handleUpdateNotifications = async (notificationsEnabled: boolean) => {
    updateProfile(
      {
        notifications_enabled: notificationsEnabled,
      },
      {
        onSuccess: () => {
          toast({
            title: 'Success',
            description: 'Your notifications have been updated',
          });
        },
        onError: (e) => {
          Sentry.captureException(e);
          toast({
            title: 'Error',
            description: 'Failed to update notifications',
            variant: 'destructive',
          });
        },
      },
    );
  };

  const { mutate: handleResetPassword, isPending: isResetLoading } =
    useMutation({
      mutationFn: async () => {
        if (!user?.email) throw new Error('User email not found');
        await resetPassword(user?.email);
      },
      onSuccess: () => {
        toast({
          title: 'Success',
          description:
            'Password reset instructions have been sent to your email',
        });
      },
      onError: () => {
        toast({
          title: 'Error',
          description: 'Failed to reset password',
          variant: 'destructive',
        });
      },
    });

  const tierLabel =
    level === 'free'
      ? 'Telekinesis Free'
      : level === 'standard'
        ? 'Telekinesis Standard'
        : 'Telekinesis Pro';

  const tierAccent =
    level === 'free'
      ? 'bg-telekinesis-neutral-700 text-telekinesis-neutral-50'
      : level === 'standard'
        ? 'bg-telekinesis-gold/15 text-[#107d40]'
        : 'bg-gradient-to-r from-telekinesis-gold/20 to-fuchsia-500/20 text-telekinesis-neutral-50';

  return (
    <div className="flex min-h-full w-full items-center justify-center bg-telekinesis-background-1 px-6 py-10">
      <div className="w-full max-w-xl">
        <header className="mb-8">
          <h1 className="text-2xl font-medium tracking-tight text-telekinesis-neutral-50">
            Settings
          </h1>
          <p className="mt-1 text-sm text-telekinesis-neutral-200">
            Manage your account, billing, and preferences.
          </p>
        </header>

        <div className="flex flex-col gap-4">
          <AccountSection
            editingName={editingName}
            setEditingName={setEditingName}
            newName={newName}
            setNewName={setNewName}
            nameInputRef={nameInputRef}
            handleUpdateName={handleUpdateName}
            isUpdateLoading={isUpdateLoading}
            profile={profile}
            user={user}
            handleResetPassword={handleResetPassword}
            isResetLoading={isResetLoading}
          />

          <DimensionsSection
            dimensionPrefs={dimensionPrefs}
            setDimensionMode={setDimensionMode}
          />

          <NotificationsSection
            profile={profile}
            handleUpdateNotifications={handleUpdateNotifications}
          />

          <BillingSection
            tierAccent={tierAccent}
            level={level}
            tierLabel={tierLabel}
            periodEnd={periodEnd}
            handleManageSubscription={handleManageSubscription}
            isManageLoading={isManageLoading}
            subscriptionTokens={subscriptionTokens}
            freeTokens={freeTokens}
            purchasedTokens={purchasedTokens}
            totalTokens={totalTokens}
            tokenPacks={tokenPacks}
            isPurchaseLoading={isPurchaseLoading}
            purchaseVariables={purchaseVariables}
            purchaseTokenPack={purchaseTokenPack}
          />

          {isAdmin && (
            <AdminSection bumpThumbnails={bumpThumbnails} isBumping={isBumping} />
          )}

          <DataPrivacySection />

          <SettingsFooter />
        </div>
      </div>
    </div>
  );
}
